import type { FavoriteItem, FavoriteKind } from "./types";

const STORAGE_KEY = "farmly:favorites:v1";
const SYNC_KEY = "farmly:favorites:lastSyncAt";
const TOMBSTONE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export function loadLocalFavorites(): FavoriteItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const items: FavoriteItem[] = JSON.parse(raw);
    // Purge old tombstones
    const cutoff = Date.now() - TOMBSTONE_MAX_AGE_MS;
    return items.filter(
      (i) => !i.deletedAt || new Date(i.deletedAt).getTime() > cutoff
    );
  } catch {
    return [];
  }
}

export function saveLocalFavorites(items: FavoriteItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // storage full — silently fail
  }
}

export function toggleLocalFavorite(
  items: FavoriteItem[],
  payload: { kind: FavoriteKind; entityId: string; slug?: string; title?: string; imagePath?: string }
): { items: FavoriteItem[]; added: boolean } {
  const key = `${payload.kind}:${payload.entityId}`;
  const idx = items.findIndex((i) => `${i.kind}:${i.entityId}` === key);

  if (idx >= 0) {
    const existing = items[idx];
    if (existing.deletedAt) {
      // Re-add (clear tombstone)
      const updated = [...items];
      updated[idx] = { ...existing, deletedAt: null, savedAt: new Date().toISOString() };
      return { items: updated, added: true };
    } else {
      // Soft delete
      const updated = [...items];
      updated[idx] = { ...existing, deletedAt: new Date().toISOString() };
      return { items: updated, added: false };
    }
  }

  // New favorite
  return {
    items: [
      ...items,
      {
        kind: payload.kind,
        entityId: payload.entityId,
        slug: payload.slug,
        title: payload.title,
        imagePath: payload.imagePath,
        savedAt: new Date().toISOString(),
        deletedAt: null,
      },
    ],
    added: true,
  };
}

export function getActiveFavorites(items: FavoriteItem[]): FavoriteItem[] {
  return items.filter((i) => !i.deletedAt);
}

export function isFavoritedLocal(
  items: FavoriteItem[],
  kind: FavoriteKind,
  entityId: string
): boolean {
  return items.some(
    (i) => i.kind === kind && i.entityId === entityId && !i.deletedAt
  );
}

export function getLastSyncAt(): string | null {
  return localStorage.getItem(SYNC_KEY);
}

export function setLastSyncAt(iso: string): void {
  localStorage.setItem(SYNC_KEY, iso);
}
